import { useState } from "react";
import type { inferRouterOutputs } from "@trpc/server";
import type { AppRouter } from "@runcheetah/api/router";
import { RELEASE_ACCESS_WAITLIST_STATUSES } from "@runcheetah/shared";
import { Button, Card, CardContent, CardHeader, CardTitle, Input } from "@runcheetah/ui";

import {
  CodeText,
  PageHeader,
  QueryErrorState,
  Select,
  TableEmptyRow,
  TableShell,
} from "@/components/common";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDateTime } from "@/lib/date-format";
import { trpc } from "@/lib/trpc";

type RouterOutputs = inferRouterOutputs<AppRouter>;
type ReleaseAccessList = RouterOutputs["admin"]["releaseAccess"]["list"];
type WaitlistEntry = ReleaseAccessList["waitlist"][number];

export const getReleaseAccessPageMeta = (data: ReleaseAccessList | undefined): string => {
  if (!data) {
    return "Loading access lists...";
  }

  return `${data.allowlist.length} allowed, ${data.waitlist.length} waitlisted`;
};

export function ReleaseAccessPage() {
  const utils = trpc.useUtils();
  const { data, isPending, error } = trpc.admin.releaseAccess.list.useQuery();
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");

  const invalidate = () => utils.admin.releaseAccess.list.invalidate();

  const addAllowlist = trpc.admin.releaseAccess.addAllowlist.useMutation({
    onSuccess: async () => {
      setEmail("");
      setNote("");
      await invalidate();
    },
  });

  const removeAllowlist = trpc.admin.releaseAccess.removeAllowlist.useMutation({
    onSuccess: invalidate,
  });
  const updateWaitlist = trpc.admin.releaseAccess.updateWaitlist.useMutation({
    onSuccess: invalidate,
  });
  const promoteWaitlist = trpc.admin.releaseAccess.promoteWaitlist.useMutation({
    onSuccess: invalidate,
  });

  if (error) {
    return (
      <QueryErrorState
        title="Failed to load release access"
        message={error.message}
        isForbidden={error.data?.code === "FORBIDDEN"}
      />
    );
  }

  const isMutating =
    addAllowlist.isPending ||
    removeAllowlist.isPending ||
    updateWaitlist.isPending ||
    promoteWaitlist.isPending;

  return (
    <div className="space-y-6">
      <PageHeader title="Release Access" meta={getReleaseAccessPageMeta(data)} />

      <Card>
        <CardHeader>
          <CardTitle>Add allowlist email</CardTitle>
        </CardHeader>
        <CardContent>
          <form
            className="grid gap-3 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]"
            onSubmit={(event) => {
              event.preventDefault();
              addAllowlist.mutate({ email, note });
            }}
          >
            <Input
              type="email"
              required
              placeholder="founder@example.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              disabled={isMutating}
            />
            <Input
              placeholder="Optional note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              disabled={isMutating}
            />
            <Button type="submit" disabled={isMutating}>
              {addAllowlist.isPending ? "Adding..." : "Add email"}
            </Button>
          </form>
          {addAllowlist.error ? (
            <p className="mt-2 text-sm text-destructive">{addAllowlist.error.message}</p>
          ) : null}
        </CardContent>
      </Card>

      <ReleaseAllowlistTable
        allowlist={data?.allowlist ?? []}
        loading={isPending}
        removingId={removeAllowlist.isPending ? removeAllowlist.variables?.id : undefined}
        onRemove={(id) => removeAllowlist.mutate({ id })}
      />

      <ReleaseWaitlistTable
        waitlist={data?.waitlist ?? []}
        loading={isPending}
        isMutating={isMutating}
        onPromote={(entry) => promoteWaitlist.mutate({ id: entry.id, note: entry.note ?? "" })}
        onUpdate={(entry, status) =>
          updateWaitlist.mutate({ id: entry.id, status, note: entry.note ?? "" })
        }
      />
    </div>
  );
}

function ReleaseAllowlistTable({
  allowlist,
  loading,
  removingId,
  onRemove,
}: {
  allowlist: ReleaseAccessList["allowlist"];
  loading: boolean;
  removingId: string | undefined;
  onRemove: (id: string) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Allowlist</CardTitle>
      </CardHeader>
      <CardContent>
        <TableShell>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Note</TableHead>
                <TableHead>Added</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? <TableEmptyRow colSpan={4}>Loading allowlist...</TableEmptyRow> : null}
              {!loading && allowlist.length === 0 ? (
                <TableEmptyRow colSpan={4}>No allowlist entries.</TableEmptyRow>
              ) : null}
              {allowlist.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell className="font-medium">{entry.email}</TableCell>
                  <TableCell className="max-w-md whitespace-normal text-muted-foreground">
                    {entry.note || "-"}
                  </TableCell>
                  <TableCell>{formatDateTime(entry.createdAt)}</TableCell>
                  <TableCell className="text-right">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={removingId === entry.id}
                      onClick={() => onRemove(entry.id)}
                    >
                      {removingId === entry.id ? "Removing..." : "Remove"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableShell>
      </CardContent>
    </Card>
  );
}

function ReleaseWaitlistTable({
  waitlist,
  loading,
  isMutating,
  onPromote,
  onUpdate,
}: {
  waitlist: WaitlistEntry[];
  loading: boolean;
  isMutating: boolean;
  onPromote: (entry: WaitlistEntry) => void;
  onUpdate: (entry: WaitlistEntry, status: WaitlistEntry["status"]) => void;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Waitlist</CardTitle>
      </CardHeader>
      <CardContent>
        <TableShell>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Brand URL</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Requested</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? <TableEmptyRow colSpan={5}>Loading waitlist...</TableEmptyRow> : null}
              {!loading && waitlist.length === 0 ? (
                <TableEmptyRow colSpan={5}>No waitlist requests.</TableEmptyRow>
              ) : null}
              {waitlist.map((entry) => (
                <TableRow key={entry.id}>
                  <TableCell className="font-medium">{entry.email}</TableCell>
                  <TableCell>
                    <a
                      href={entry.brandUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-primary hover:underline"
                    >
                      {entry.brandUrl}
                    </a>
                  </TableCell>
                  <TableCell>
                    <Select
                      value={entry.status}
                      disabled={isMutating}
                      onChange={(event) =>
                        onUpdate(entry, event.target.value as WaitlistEntry["status"])
                      }
                    >
                      {RELEASE_ACCESS_WAITLIST_STATUSES.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </Select>
                  </TableCell>
                  <TableCell>{formatDateTime(entry.createdAt)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <CodeText>{entry.id.slice(0, 8)}</CodeText>
                      <Button
                        type="button"
                        size="sm"
                        disabled={isMutating || entry.status === "approved"}
                        onClick={() => onPromote(entry)}
                      >
                        Promote
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableShell>
      </CardContent>
    </Card>
  );
}
